import { Grid } from '@mui/material';
import { useTheme } from '@mui/styles';
import { ApplicationMuiTheme } from '@tassat/payments-themes/dist/types/theme';
import { ActionTypes, SelectOption } from '@tassat/ui-definitions';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import React, { Reducer, useContext, useReducer } from 'react';

import { FormContents } from './AccountCreationFormContents';
import { validateAccountForm } from './submission';
import { IFormValues, IFormikProps } from './types';
import { ConstantsContext, FALLBACK_FIS_TIMEOUT } from '../helpers/constant';
import {
    FieldValidationAction,
    FieldValidationState,
    fieldValidationReducer,
    setInitialState
} from '../helpers/fieldValidationReducer';

export default function AccountForm(props: IFormikProps) {
    const {
        onClose,
        formProps
    } = props;

    const {
        actionType,
        allAccounts,
        firmAccounts,
        validateDdaAccount,
        submitAccountForm,
        firm
    } = formProps;

    const theme = useTheme<ApplicationMuiTheme>();
    const uiConstants = useContext(ConstantsContext);
    const API_TIMEOUT = (uiConstants && uiConstants.FIS_DDA_TIMEOUT) || FALLBACK_FIS_TIMEOUT;

    const [
        ddaAccountState,
        dispatchDdaAccountAction
    ] = useReducer<Reducer<
        FieldValidationState<SelectOption>,
        FieldValidationAction<SelectOption>
    >>(fieldValidationReducer, setInitialState());
    const [ submitError, setSubmitError ] = React.useState<string>('');

    let initialValues = {} as IFormValues;
    if (actionType === ActionTypes.add) {
        initialValues = {
            AccountName: '',
            Account: '',
            Notes: '',
            Email: ''
        };
    } else if (actionType === ActionTypes.edit) {
        const editAccount = formProps.editAccount;
        if (!editAccount) return <div />;

        initialValues = {
            AccountName: editAccount.AccountName,
            Account: editAccount.Account,
            Notes: editAccount.Notes,
            Email: editAccount.Email
        };
    }

    const ddaLengthObj = { ...theme?.app?.clientTheme.formConstants };
    const variableDDALength = ddaLengthObj.ddaLengthMin !== ddaLengthObj.ddaLengthMax;

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                item
                xs={12}
            >
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values: IFormValues, formikHelpers: FormikHelpers<IFormValues>) => {
                        const errors = variableDDALength &&
                            (values?.Account?.length !== ddaLengthObj.ddaLengthMax) &&
                            await formikHelpers.validateForm(values);

                        if (errors && Object.keys(errors).length) return formikHelpers.setErrors(errors);

                        await submitAccountForm(values, {
                            actionType: actionType,
                            firmId: firm.FirmId,
                            account: actionType === ActionTypes.edit ?
                                formProps.editAccount
                                : undefined,
                            onClose: onClose,
                            setSubmitError: setSubmitError
                        });

                        return;
                    }}
                    validate={(values: IFormValues) => validateAccountForm(values, {
                        actionType,
                        initialValues,
                        firm,
                        firmAccounts,
                        allAccounts,
                        validateDdaAccount,
                        ddaAccountState,
                        dispatchDdaAccountAction,
                        variableDDALength,
                        ddaLengthObj,
                        API_TIMEOUT
                    })}
                    validateOnBlur={false}
                    validateOnChange
                    validateOnMount
                >
                    {(formikProps: FormikProps<IFormValues>) => (
                        <FormContents
                            {...formikProps}
                            dispatchDdaAccountAction={dispatchDdaAccountAction}
                            formProps={formProps}
                            submitError={submitError}
                        />
                        )}
                </Formik>
            </Grid>
        </Grid>
    );
}